import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTournamentEntrants,
  confirmEntrant,
  withdrawFromTournament,
  updateEntrantSeed,
} from '../services/tournamentsV2';
import { TournamentEntrant } from '../types';
import './TournamentParticipantManagement.css';

interface TournamentParticipantManagementProps {
  tournamentId: number;
  tournamentName: string;
  isOpen: boolean;
  onClose: () => void;
  inline?: boolean; // New: render inline without modal wrapper
}

const TournamentParticipantManagement: React.FC<TournamentParticipantManagementProps> = ({
  tournamentId,
  tournamentName,
  isOpen,
  onClose,
  inline = false,
}) => {
  const { t } = useTranslation();
  const [entrants, setEntrants] = useState<TournamentEntrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen) {
      loadEntrants();
    }
  }, [isOpen, tournamentId]);

  const loadEntrants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTournamentEntrants(tournamentId);
      setEntrants(data);
    } catch (err) {
      console.error('Error loading entrants:', err);
      setError(t('tournaments.participants.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (entrantId: string) => {
    try {
      setProcessing(prev => ({ ...prev, [entrantId]: true }));
      await confirmEntrant(entrantId);
      await loadEntrants();
    } catch (err) {
      console.error('Error confirming entrant:', err);
      alert(t('tournaments.participants.confirmError'));
    } finally {
      setProcessing(prev => ({ ...prev, [entrantId]: false }));
    }
  };

  const handleWithdraw = async (entrantId: string, playerName: string) => {
    const confirmed = window.confirm(
      t('tournaments.participants.withdrawConfirm', { name: playerName })
    );
    if (!confirmed) return;

    try {
      setProcessing(prev => ({ ...prev, [entrantId]: true }));
      await withdrawFromTournament(entrantId);
      await loadEntrants();
    } catch (err) {
      console.error('Error withdrawing entrant:', err);
      alert(t('tournaments.participants.withdrawError'));
    } finally {
      setProcessing(prev => ({ ...prev, [entrantId]: false }));
    }
  };

  const handleSeedChange = async (entrantId: string, newSeed: number) => {
    try {
      await updateEntrantSeed(entrantId, newSeed);
      await loadEntrants();
    } catch (err) {
      console.error('Error updating seed:', err);
      alert(t('tournaments.participants.seedError'));
    }
  };

  if (!isOpen) return null;

  // Group entrants by status
  const registeredEntrants = entrants.filter(e => e.status === 'registered');
  const confirmedEntrants = entrants.filter(e => e.status === 'confirmed');
  const withdrawnEntrants = entrants.filter(e => e.status === 'withdrawn');

  // Content to render (same for both modal and inline)
  const content = (
    <>
      {/* Header - only for modal */}
      {!inline && (
        <div className="modal-header">
          <h3>{t('tournaments.participants.title')} - {tournamentName}</h3>
          <button onClick={onClose} className="close-button">&times;</button>
        </div>
      )}

      <div className={inline ? "participant-management-inline" : "modal-body"}>
          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              {/* Pending Registrations */}
              {registeredEntrants.length > 0 && (
                <div className="participant-section">
                  <h4 className="section-title">
                    {t('tournaments.participants.pendingRegistrations')} ({registeredEntrants.length})
                  </h4>
                  <div className="participants-list">
                    {registeredEntrants.map(entrant => (
                      <div key={entrant.id} className="participant-card pending">
                        <div className="participant-info">
                          <div className="participant-name">
                            {entrant.player?.preName} {entrant.player?.name}
                          </div>
                          <div className="participant-meta">
                            {t('tournaments.participants.registeredAt')}: {new Date(entrant.registered_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="participant-actions">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleConfirm(entrant.id)}
                            disabled={processing[entrant.id]}
                          >
                            {processing[entrant.id] 
                              ? t('common.processing') 
                              : t('tournaments.participants.confirm')}
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleWithdraw(
                              entrant.id, 
                              `${entrant.player?.preName} ${entrant.player?.name}`
                            )}
                            disabled={processing[entrant.id]}
                          >
                            {t('tournaments.participants.reject')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Confirmed Participants */}
              <div className="participant-section">
                <h4 className="section-title">
                  {t('tournaments.participants.confirmedParticipants')} ({confirmedEntrants.length})
                </h4>
                {confirmedEntrants.length === 0 ? (
                  <div className="empty-state">{t('tournaments.participants.noConfirmed')}</div>
                ) : (
                  <div className="participants-list">
                    {confirmedEntrants.map(entrant => (
                      <div key={entrant.id} className="participant-card confirmed">
                        <div className="participant-info">
                          <div className="participant-seed">
                            {entrant.seed ? (
                              <span className="seed-badge">#{entrant.seed}</span>
                            ) : (
                              <span className="seed-badge unseeded">-</span>
                            )}
                          </div>
                          <div className="participant-details">
                            <div className="participant-name">
                              {entrant.player?.preName} {entrant.player?.name}
                            </div>
                            <div className="participant-meta">
                              {t('tournaments.participants.confirmedAt')}: {new Date(entrant.registered_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="participant-actions">
                          <input
                            type="number"
                            min="1"
                            value={entrant.seed || ''}
                            onChange={(e) => handleSeedChange(entrant.id, parseInt(e.target.value) || 0)}
                            placeholder={t('tournaments.participants.seedPlaceholder')}
                            className="seed-input"
                          />
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleWithdraw(
                              entrant.id, 
                              `${entrant.player?.preName} ${entrant.player?.name}`
                            )}
                            disabled={processing[entrant.id]}
                          >
                            {t('tournaments.participants.remove')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Withdrawn Participants */}
              {withdrawnEntrants.length > 0 && (
                <div className="participant-section">
                  <h4 className="section-title">
                    {t('tournaments.participants.withdrawn')} ({withdrawnEntrants.length})
                  </h4>
                  <div className="participants-list">
                    {withdrawnEntrants.map(entrant => (
                      <div key={entrant.id} className="participant-card withdrawn">
                        <div className="participant-info">
                          <div className="participant-name">
                            {entrant.player?.preName} {entrant.player?.name}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="participant-summary">
                <div className="summary-item">
                  <span className="summary-label">{t('tournaments.participants.total')}:</span>
                  <span className="summary-value">{entrants.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">{t('tournaments.participants.confirmed')}:</span>
                  <span className="summary-value">{confirmedEntrants.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">{t('tournaments.participants.pending')}:</span>
                  <span className="summary-value">{registeredEntrants.length}</span>
                </div>
              </div>
            </>
          )}
      </div>

      {/* Footer - only for modal */}
      {!inline && (
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            {t('common.close')}
          </button>
        </div>
      )}
    </>
  );

  // Render as modal or inline
  if (inline) {
    return <div className="participant-management-wrapper">{content}</div>;
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content participant-management-modal">
        {content}
      </div>
    </div>
  );
};

export default TournamentParticipantManagement;

